import type {Dayjs} from "dayjs";
import {useCallback, useState, useMemo} from "react";
import {MapLayerMouseEvent} from "react-map-gl";
import {getCropCycleForDate} from "../utils/fieldUtils";
import {Field} from "../types/domain/field";
import {useCrops} from "./cropsHooks";
import {useSoils} from "./soilsHooks";
import {useIrrigations} from "./irrigationsHooks";
import {getCenter} from "../utils/mapUtils";

export interface PopupInfo {
    feature: Field;
    lngLat: {lng: number; lat: number};
}

export interface Props {
    header: string;
    value: string;
}

export const useFieldPopup = (fields: Field[] | undefined, layerId: string, date: Dayjs | null) => {
    const {cropsById} = useCrops();
    const {soilsById} = useSoils();
    const {irrigationsById} = useIrrigations();
    const [hoverInfo, setHoverInfo] = useState<PopupInfo>();

    const onHover = useCallback(
        (event: MapLayerMouseEvent) => {
            const {features} = event;

            if (!fields) {
                setHoverInfo(undefined);
                return;
            }
            const hoveredFeature = features?.find((f) => f.layer?.id === layerId);
            if (hoveredFeature) {
                const fieldInfo = fields.find((field) => field.id === hoveredFeature.properties?.id);
                if (fieldInfo) {
                    const lngLat = getCenter(fieldInfo.geometry!);
                    setHoverInfo({
                        feature: fieldInfo,
                        lngLat: lngLat,
                    });
                } else {
                    setHoverInfo(undefined);
                }
            }
        },
        [fields, layerId],
    );

    const onLeave = useCallback(() => {
        setHoverInfo(undefined);
    }, []);

    const popupDetails = useMemo(() => {
        if (!hoverInfo) {
            return null;
        }
        const f = hoverInfo.feature;
        const irType = irrigationsById[f.irrigationTypeId]?.name || "";
        const soilType = soilsById[f.soilId]?.name || "";
        let cropType = "";
        let plantedDate = "";
        const cc = getCropCycleForDate(f, date);
        if (cc) {
            cropType = cropsById[cc.cropId]?.name || "";
            plantedDate = cc.plantedDate ? cc.plantedDate.format("YYYY-MM-DD") : "";
        }
        return {
            featureName: hoverInfo.feature.name,
            cropType,
            plantedDate,
            soilType,
            irType,
            lngLat: hoverInfo.lngLat,
        };
    }, [hoverInfo, cropsById, soilsById, irrigationsById, date]);

    return {
        onHover,
        onLeave,
        popupDetails,
    };
};
